import { ServerFieldErrors } from './useServerErrors';

// Container-held server validation errors, keyed by the UI step that owns them
// (D-24094 #20). Lifted out of JobStepper so both the container state and this pure
// helper share one shape.
export interface StepServerErrors {
  /** UI step index -> that step's routed field errors. */
  byStep: Record<number, ServerFieldErrors>;
  /** Lowest step index still blamed — the navigation target. */
  lowestStep: number;
}

/**
 * Drop one step's stored server-error slice after that step re-validates, and
 * recompute the navigation target from what remains.
 *
 * Returns `null` when no slices remain: the caller must NOT keep an object whose
 * `lowestStep` is `Math.min(...[]) === Infinity`, or the navigation effect in
 * JobStepper would call `goToStep(Infinity)`.
 *
 * When `stepIndex` owns no slice (or there are no errors at all) the SAME reference
 * is returned, so a `setServerErrors(prev => dropStepSlice(prev, n))` updater bails
 * out on Object.is equality and triggers no re-render.
 */
export const dropStepSlice = (
  serverErrors: StepServerErrors | null,
  stepIndex: number
): StepServerErrors | null => {
  if (!serverErrors || !(stepIndex in serverErrors.byStep)) {
    return serverErrors;
  }

  const byStep = { ...serverErrors.byStep };
  delete byStep[stepIndex];

  const remaining = Object.keys(byStep).map(Number);
  if (!remaining.length) {
    return null;
  }

  return { byStep, lowestStep: Math.min(...remaining) };
};

export default dropStepSlice;
